/**
 * AddContentSheetContent
 *
 * Stepper-based content for the video upload sheet.
 * Steps: 1. Drop Video → 2. Video Preview → 3. Description → 4. Content Settings → 5. Monetization
 * Follows ReportContentSheetContent pattern.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ExpauseStepper, type ExpauseStepperRef } from '@/presentation/components/common/stepper/ExpauseStepper';
import { ReportTopBar } from '@/presentation/components/common/ReportTopBar';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useExpauseLocalizations } from '@/presentation/locale/expauseLocalizations';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { AddContentFlowType } from '@/domain/enums/publish/addContentFlowType';
import { isMessageFlowType } from '@/domain/utils/addContentFlowPredicates';
import { qaAttr } from '@/presentation/utils/qaAttr';
import type {
  ChatAddContentOptions,
  CommunityAddContentOptions,
  FeedbackAddContentOptions,
  ProfileImageAddContentOptions,
  CommentReplyAddContentOptions,
} from '../AddContentSheet';
import { useAddContent } from '../hooks/useAddContent';
import { useAddContentSubmitSeams } from '../hooks/useAddContentSubmitSeams';
import { DropVideoStep } from './steps/DropVideoStep';
import { VideoPreviewStep } from './steps/VideoPreviewStep';
import { resolveDropStepConfig } from './resolveDropStepConfig';
import { buildUserFlowSteps } from './userFlowSteps';

/**
 * Each flow discriminator is documented by the option interface that IS its
 * type — see `AddContentSheet.tsx`. Absent all five → the standard
 * user-content flow (all five steps).
 */
interface AddContentSheetContentProps {
  onFinish: () => void;
  screenKey?: string;
  /** Presence drives the chat-media flow (Drop + Preview). See `ChatAddContentOptions`. */
  chat?: ChatAddContentOptions;
  /** Presence drives the community-media flow (images + videos). See `CommunityAddContentOptions`. */
  community?: CommunityAddContentOptions;
  /** Presence drives the feedback flow (image-only, collect-not-send). See `FeedbackAddContentOptions`. */
  feedback?: FeedbackAddContentOptions;
  /** Presence drives the profile-image flow (image-only, collect-not-send). See `ProfileImageAddContentOptions`. */
  profileImage?: ProfileImageAddContentOptions;
  /** Presence drives the comment-reply flow (full 5-step user flow, "Reply comment" picker title). See `CommentReplyAddContentOptions`. */
  commentReply?: CommentReplyAddContentOptions;
}

export const AddContentSheetContent: React.FC<AddContentSheetContentProps> = ({
  onFinish,
  screenKey,
  chat,
  community,
  feedback,
  profileImage,
  commentReply,
}) => {
  const { l10n } = useLocale();
  const { expauseLocalizations } = useExpauseLocalizations();
  const { theme } = useTheme();
  const stepperRef = useRef<ExpauseStepperRef>(null);
  const [currentStep, setCurrentStep] = useState(0);

  const isCommunityFlowParam = community !== undefined;
  const isFeedbackFlowParam = feedback !== undefined;
  const isProfileImageFlowParam = profileImage !== undefined;
  const isCommentReplyFlowParam = commentReply !== undefined;

  const {
    flowType,
    selectedFile,
    videoMeta,
    mediaType,
    validationError,
    isProcessing,
    pendingNavigation,
    descriptionResult,
    contentSettings,
    userMediaData,
    clearPendingNavigation,
    handleFileDrop,
    handleVideoConfirm,
    handleDescriptionComplete,
    handleSettingsComplete,
    handleBack: onBack,
  } = useAddContent({
    chatUsersInfo: chat?.chatUsersInfo,
    isCommunityFlow: isCommunityFlowParam,
    isFeedbackFlow: isFeedbackFlowParam,
    isProfileImageFlow: isProfileImageFlowParam,
    isCommentReplyFlow: isCommentReplyFlowParam,
  });

  // NOT a message flow — the comment-reply flow runs the full five-step user
  // chain and only swaps the step-1 title (see `getAddContentStepTitles`).
  const isCommentReplyFlow = flowType === AddContentFlowType.comment;
  // The trimmed "message media" flows (Drop + Preview, "Done" primary action,
  // no monetization overlays) — see `isMessageFlowType` for the membership.
  const isMessageFlow = isMessageFlowType(flowType);

  // Drop-step config resolves per flow (community + chat = images + videos with
  // the image-or-video copy, user = DropVideoStep video-only defaults).
  // Extracted to a pure resolver to flatten the per-flow ternaries — see
  // `resolveDropStepConfig` for the per-flow rationale.
  const { accept: dropAccept, title: dropTitle, description: dropDescription } =
    resolveDropStepConfig(flowType, l10n);

  // The Preview "Done" seam per flow (chat/community send; feedback/profileImage
  // collect-not-send), resolved from the single `flowType` discriminator.
  // Extracted to keep this component under the size ceiling — see the hook for
  // the per-flow rationale.
  const { handleMessageSubmit } = useAddContentSubmitSeams({
    flowType,
    selectedFile,
    mediaType,
    videoMeta,
    onFinish,
    screenKey,
    chat,
    community,
    feedback,
    profileImage,
  });

  useEffect(() => {
    if (pendingNavigation) {
      stepperRef.current?.goNext();
      clearPendingNavigation();
    }
  }, [pendingNavigation, clearPendingNavigation]);

  const handleBack = () => {
    if (currentStep === 0) return;
    onBack(currentStep);
    stepperRef.current?.goPrevious();
  };

  const steps = useMemo(() => {
    const dropStep = {
      id: 'drop-video',
      content: (
        <DropVideoStep
          validationError={validationError}
          isProcessing={isProcessing}
          onFileDrop={handleFileDrop}
          onCancel={onFinish}
          // Community + chat = videos + images; user flow stays videos-only
          // (DropVideoStep default).
          accept={dropAccept}
          // Community / chat override the heading + description; user flow uses
          // DropVideoStep's video-only defaults.
          title={dropTitle}
          description={dropDescription}
        />
      ),
    };

    const previewStep = {
      id: 'video-preview',
      content: (
        <VideoPreviewStep
          file={selectedFile}
          videoMeta={videoMeta}
          onConfirm={handleVideoConfirm}
          onCancel={() => handleBack()}
          isMessageFlow={isMessageFlow}
          onMessageSubmit={handleMessageSubmit}
          mediaType={mediaType}
        />
      ),
    };

    // Chat + community flows stop at the preview (Flutter
    // preview_content_screen.dart:119-172); the user flow continues through
    // description / settings / monetization.
    if (isMessageFlow) {
      return [dropStep, previewStep];
    }

    return [
      dropStep,
      previewStep,
      ...buildUserFlowSteps({
        mediaId: userMediaData.mediaId,
        selectedFile,
        videoMeta,
        descriptionResult,
        contentSettings,
        onDescriptionComplete: handleDescriptionComplete,
        onSettingsComplete: handleSettingsComplete,
        onBack: () => handleBack(),
        onFinish,
        screenKey,
        // Comment-reply flow only — all three are `undefined`/`false` for the
        // plain user flow, so nothing changes for the shipped publish entry
        // points.
        contentReplyCommentInfo: commentReply?.contentReplyCommentInfo ?? null,
        onUploaded: commentReply?.onUploaded,
        // Flutter renders an empty `Container()` in place of the schedule
        // widget when `contentReplyCommentInfo != null`
        // (`content_settings_screen.dart:565-568`) — the sole structural
        // difference between the two publish chains.
        hideSchedule: isCommentReplyFlow,
      }),
    ];
  }, [
    isMessageFlow, isCommentReplyFlow, validationError, isProcessing, selectedFile, videoMeta, mediaType,
    descriptionResult, contentSettings, userMediaData.mediaId,
    handleFileDrop, handleVideoConfirm, handleMessageSubmit,
    handleDescriptionComplete, handleSettingsComplete, onFinish, currentStep, screenKey,
    dropAccept, dropTitle, dropDescription, commentReply,
  ]);

  // Per-step app-bar titles resolve from the single `flowType` discriminator —
  // a value→labels lookup, so it lives with the other conditional translation
  // lookups in `useExpauseLocalizations`. It owns the message-flow vs
  // five-step-chain split and the comment-reply step-1 title override.
  const stepTitles = expauseLocalizations.getAddContentStepTitles(flowType);

  // Reuse the step ids already declared in `steps` so the qa-tester can read the
  // active step from the sheet root without re-tagging each step's container.
  const currentStepStatus = steps[currentStep]?.id ?? steps[0]?.id;

  return (
    <div
      {...qaAttr({ id: 'add-content-sheet', status: currentStepStatus })}
      style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
    >
      <ReportTopBar
        title={stepTitles[currentStep] ?? stepTitles[0]}
        showBack={currentStep > 0}
        onBack={handleBack}
        onClose={onFinish}
      />
      <div style={{ flex: 1, minHeight: 0 }}>
        <ExpauseStepper
          ref={stepperRef}
          progressProps={{ showProgress: true, backgroundColor: theme.transparent }}
          initialStep={0}
          animationType="slide"
          onStepChanged={setCurrentStep}
          steps={steps}
        />
      </div>
    </div>
  );
};
